import React from 'react';
import { captureException, withScope } from '@sentry/browser';
import { isError } from 'lodash';
import c from 'classnames';
import './ErrorBoundary.styl';

function isAtLeastReact17(version: string): boolean {
  const major = version.match(/^([^.]+)/);
  return major !== null && parseInt(major[0]) >= 17;
}

export type ErrorBoundaryProps = {
  children?: React.ReactNode | (() => React.ReactNode);
  className?: string;
  style?: React.CSSProperties;
  fallback?: React.ReactElement;
};

type ErrorBoundaryState = {
  error: Error | null;
};

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  componentDidCatch(error: Error & { cause?: Error }, { componentStack }: React.ErrorInfo): void {

    withScope(() => {
      // If on React version >= 17, create stack trace from componentStack param and links
      // to to the original error using `error.cause` otherwise relies on error param for stacktrace.
      // Linking errors requires the `LinkedErrors` integration be enabled.
      // See: https://reactjs.org/blog/2020/08/10/react-v17-rc.html#native-component-stacks
      //
      // Although `componentDidCatch` is typed to accept an `Error` object, it can also be invoked
      // with non-error objects. This is why we need to check if the error is an error-like object.
      // See: https://github.com/getsentry/sentry-javascript/issues/6167
      if (isAtLeastReact17(React.version) && isError(error)) {
        const errorBoundaryError = new Error(error.message);
        errorBoundaryError.name = `React ErrorBoundary ${errorBoundaryError.name}`;
        errorBoundaryError.stack = componentStack;

        // Using the `LinkedErrors` integration to link the errors together.
        error.cause = errorBoundaryError;
      }

      captureException(error, { contexts: { react: { componentStack } } });

      // componentDidCatch is used over getDerivedStateFromError
      // so that componentStack is accessible through state.
      this.setState({ error });
    });
  }

  render(): React.ReactNode {
    const { fallback, children, className, style } = this.props;
    const { error } = this.state;

    if (error) {
      if (React.isValidElement(fallback)) {
        return fallback;
      }

      return (
        <div
          className={c("mio-error-boundary", className)}
          style={style}
        >
          <span>
            Something went wrong
            <br />
            Errors have been reported
          </span>
        </div>
      );
    }

    if (typeof children === 'function') {
      return (children as () => React.ReactNode)();
    }
    return children;
  }
}

export default ErrorBoundary;
